import { useState, useEffect } from 'react';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  platform: 'ios' | 'android' | 'web' | 'windows' | 'macos';
  isNative: boolean;
  isPWA: boolean;
  isInstallable: boolean;
  screenSize: 'small' | 'medium' | 'large' | 'xlarge';
  orientation: 'portrait' | 'landscape';
}

export interface Breakpoints {
  mobile: string;
  tablet: string;
  desktop: string;
  large: string;
}

const defaultBreakpoints: Breakpoints = {
  mobile: '(max-width: 767px)', // Mobile-first: anything below 768px is mobile
  tablet: '(min-width: 768px) and (max-width: 1023px)',
  desktop: '(min-width: 1024px) and (max-width: 1439px)',
  large: '(min-width: 1440px)'
};

export const useDeviceDetection = (customBreakpoints?: Partial<Breakpoints>) => {
  const breakpoints = { ...defaultBreakpoints, ...customBreakpoints };
  
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        platform: 'web',
        isNative: false,
        isPWA: false,
        isInstallable: false,
        screenSize: 'large',
        orientation: 'landscape'
      };
    }
    return getDeviceInfo(breakpoints);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateDeviceInfo = () => {
      setDeviceInfo(getDeviceInfo(breakpoints));
    };

    // Listen for screen size changes
    const mediaQueries = Object.values(breakpoints).map(query => 
      window.matchMedia(query)
    );

    mediaQueries.forEach(mq => {
      mq.addEventListener('change', updateDeviceInfo);
    });

    // Listen for orientation changes
    window.addEventListener('orientationchange', updateDeviceInfo);
    window.addEventListener('resize', updateDeviceInfo);

    return () => {
      mediaQueries.forEach(mq => {
        mq.removeEventListener('change', updateDeviceInfo);
      });
      window.removeEventListener('orientationchange', updateDeviceInfo);
      window.removeEventListener('resize', updateDeviceInfo);
    };
  }, [breakpoints]);

  return deviceInfo;
};

function getDeviceInfo(breakpoints: Breakpoints): DeviceInfo {
  const userAgent = navigator.userAgent;
  const width = window.innerWidth;
  const height = window.innerHeight;
  
  // Platform detection
  let platform: DeviceInfo['platform'] = 'web';

  if (/iPad|iPhone|iPod/.test(userAgent)) {
    platform = 'ios';
  } else if (/Android/.test(userAgent)) {
    platform = 'android';
  } else if (/Windows/.test(userAgent)) {
    platform = 'windows';
  } else if (/Macintosh/.test(userAgent)) {
    platform = 'macos';
  }

  // Check PWA and installability
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;
  
  // Check if app is installable
  const isInstallable = 'serviceWorker' in navigator && 
                       'BeforeInstallPromptEvent' in window;

  // Enhanced mobile detection - combine screen size + user agent
  const isMobileQuery = window.matchMedia(breakpoints.mobile);
  const isTabletQuery = window.matchMedia(breakpoints.tablet);
  
  // Check if mobile device by user agent (iOS, Android)
  const isMobileDevice = /iPad|iPhone|iPod|Android/i.test(userAgent);
  
  const isMobile = isMobileQuery.matches || isMobileDevice;
  const isTablet = isTabletQuery.matches && !isMobileDevice;
  const isDesktop = !isMobile && !isTablet;

  // Screen size categories
  let screenSize: DeviceInfo['screenSize'] = 'large';
  if (width < 576) {
    screenSize = 'small';
  } else if (width < 768) {
    screenSize = 'medium';
  } else if (width < 1200) {
    screenSize = 'large';
  } else {
    screenSize = 'xlarge';
  }

  // Orientation
  const orientation: DeviceInfo['orientation'] = height > width ? 'portrait' : 'landscape';

  return {
    isMobile,
    isTablet,
    isDesktop,
    platform,
    isNative: false,
    isPWA,
    isInstallable,
    screenSize,
    orientation
  };
}

// Utility hook for specific device checks
export const useIsMobile = () => {
  const { isMobile } = useDeviceDetection();
  return isMobile;
};

export const useIsTablet = () => {
  const { isTablet } = useDeviceDetection();
  return isTablet;
};

export const useIsDesktop = () => {
  const { isDesktop } = useDeviceDetection();
  return isDesktop;
};

export const useIsNative = () => {
  const { isNative } = useDeviceDetection();
  return isNative;
};

export const usePlatform = () => {
  const { platform } = useDeviceDetection();
  return platform;
};

export const useOrientation = () => {
  const { orientation } = useDeviceDetection();
  return orientation;
};

// CSS-in-JS helper for responsive styles
export const responsive = {
  mobile: '@media (max-width: 767px)',
  tablet: '@media (min-width: 768px) and (max-width: 1023px)',
  desktop: '@media (min-width: 1024px)',
  portrait: '@media (orientation: portrait)',
  landscape: '@media (orientation: landscape)'
};

export default useDeviceDetection;